/** 
 * @module PlanGenerator
 * @description Parent component that conditionally renders SEEMP II, SEEMP III, MRV, or FEU monitoring plan
 * components based on the selected plan type and user feature access.
 */
import { useLocation } from "react-router-dom";
import { useState, useContext, useMemo } from "react";
import SeempMonitoringPlanII from "../../MonitoringPlans/SEEMP/SeempII/SeempMonitoringPlanII";
import SeempMonitoringPlanIII from "../../MonitoringPlans/SEEMP/SeempIII/SeempMonitoringPlanIII";
import MrvMonitoringPlan from "../../MonitoringPlans/Mrv/MrvMonitoringPlan";
import FuelEuMonitoringPlan from "../../MonitoringPlans/FuelEu/FuelEumonitoring";
import { ClaimsContext } from "../../../services/ClaimsService";
import { FeatureType } from "../../../constants/FeatureToggles";

/**
 * @param {object} props
 * @param {object} props.planVerificationDetails full verification details (contains Plan, VesselMstId, VerificationStatus, etc.)
 * @param {object} props.planVerificationFlags server-provided flags (includes corrections state)
 * @param {boolean} [props.shouldClone] initial FEU cloning flag
 * @param {Function} props.refreshState callback to refresh verification state
 */
const PlanGenerator = ({
  planVerificationDetails,
  planVerificationFlags,
  shouldClone: initialShouldClone,
  refreshState
}) => {
  const [shouldClone, setShouldClone] = useState(initialShouldClone ?? false);
  const { userHasFeature, userFeaturesAreLoaded } = useContext(ClaimsContext);
  const location = useLocation();
  const wcnNumber = location.state?.wcnNumber;

  // Feature availability checks
  const canSeemp2 = useMemo(
    () =>
      userFeaturesAreLoaded &&
      (userHasFeature(FeatureType.CreateSeemp2MonitoringPlan) ||
        userHasFeature(FeatureType.ViewSeemp2MonitoringPlan)),
    [userFeaturesAreLoaded, userHasFeature]
  );

  const canSeemp3 = useMemo(
    () =>
      userFeaturesAreLoaded &&
      (userHasFeature(FeatureType.CreateSeemp3MonitoringPlan) ||
        userHasFeature(FeatureType.ViewSeemp3MonitoringPlan)),
    [userFeaturesAreLoaded, userHasFeature]
  );

  const canMrv = useMemo(
    () =>
      userFeaturesAreLoaded &&
      (userHasFeature(FeatureType.CreateMrvMonitoringPlan) ||
        userHasFeature(FeatureType.ViewMrvMonitoringPlan)),
    [userFeaturesAreLoaded, userHasFeature]
  );

  const canFeu = useMemo(
    () =>
      userFeaturesAreLoaded &&
      (userHasFeature(FeatureType.CreateFuelEuMonitoringPlan) ||
        userHasFeature(FeatureType.ViewFuelEuMonitoringPlan)),
    [userFeaturesAreLoaded, userHasFeature]
  );

  // Wait until feature flags are loaded
  if (!userFeaturesAreLoaded) return null;

  return (
    <div>
      {planVerificationDetails.Plan === "SEEMP II" && canSeemp2 ? (
        <SeempMonitoringPlanII
          vesselMstId={planVerificationDetails.VesselMstId}
          status={planVerificationDetails.VerificationStatus}
          planVerificationDetails={planVerificationDetails}
          planVerificationFlags={planVerificationFlags}
          wcnNumber={wcnNumber}
          refreshState={refreshState}
        />
      ) : planVerificationDetails.Plan === "SEEMP III" && canSeemp3 ? (
        <SeempMonitoringPlanIII
          vesselMstId={planVerificationDetails.VesselMstId}
          status={planVerificationDetails.VerificationStatus}
          planVerificationDetails={planVerificationDetails}
          planVerificationFlags={planVerificationFlags}
          wcnNumber={wcnNumber}
          refreshState={refreshState}
        />
      ) : null}

      {planVerificationDetails.Plan === "EU MRV MP" && canMrv ? (
        <MrvMonitoringPlan vesselMstId={planVerificationDetails.VesselMstId} />
      ) : planVerificationDetails.Plan === "FEU MP" && canFeu ? (
        <FuelEuMonitoringPlan
          vesselMstId={planVerificationDetails.VesselMstId}
          shouldClone={shouldClone}
          setShouldClone={setShouldClone}
        />
      ) : null}
    </div>
  );
};

export default PlanGenerator;